import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { changeTheme, ThemeType } from '~/store/settingsSlice'
import { RootState } from '~/store'
import { cm } from '~/utils/utils'

/**
 * A sun icon used for the "light" theme option.
 *
 * @param className - Optional Tailwind classes forwarded to the <svg>.
 * @returns The sun glyph as an inline SVG.
 */
function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
      className={className}
      aria-hidden
    >
      <circle cx='12' cy='12' r='4' />
      <path d='M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41' />
    </svg>
  )
}

/**
 * A crescent-moon icon used for the "dark" theme option.
 *
 * @param className - Optional Tailwind classes forwarded to the <svg>.
 * @returns The moon glyph as an inline SVG.
 */
function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
      className={className}
      aria-hidden
    >
      <path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' />
    </svg>
  )
}

/**
 * A monitor icon used for the "system" (follow-OS) theme option.
 *
 * @param className - Optional Tailwind classes forwarded to the <svg>.
 * @returns The monitor glyph as an inline SVG.
 */
function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
      className={className}
      aria-hidden
    >
      <rect x='2' y='3' width='20' height='14' rx='2' />
      <path d='M8 21h8M12 17v4' />
    </svg>
  )
}

/** One selectable theme option rendered inside the toggle. */
const OPTIONS: { value: ThemeType; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { value: 'light', label: 'Light', Icon: SunIcon },
  { value: 'dark', label: 'Dark', Icon: MoonIcon },
  { value: 'system', label: 'System', Icon: MonitorIcon }
]

export interface ThemeToggleProps extends React.HTMLAttributes<HTMLDivElement> {
  /** When true, hides the text labels and shows icon-only buttons. */
  iconOnly?: boolean
}

/**
 * Segmented control that lets the user pick the app color theme.
 *
 * Reads the current theme from the Redux `settings` slice and dispatches
 * `changeTheme` on selection. The actual `.dark` / `.light` class on <html>
 * is applied by the theme effect in `pages/index.tsx`, and the choice is
 * persisted to localStorage by the settings slice middleware.
 *
 * @param iconOnly - Render icon-only buttons (compact) when true.
 * @param className - Extra classes merged onto the container.
 * @returns The theme segmented control.
 */
export const ThemeToggle = React.forwardRef<HTMLDivElement, ThemeToggleProps>(
  ({ iconOnly = false, className, ...props }, ref) => {
    const dispatch = useDispatch()
    const theme = useSelector((state: RootState) => state.settings.theme) as ThemeType

    return (
      <div
        ref={ref}
        role='radiogroup'
        aria-label='Color theme'
        className={cm(
          'flex w-full items-center gap-1 rounded-full bg-tokens-neutral-fill-fill-6 p-1 select-none',
          className
        )}
        {...props}
      >
        {OPTIONS.map(({ value, label, Icon }) => {
          const isActive = theme === value
          return (
            <button
              key={value}
              type='button'
              role='radio'
              aria-checked={isActive}
              aria-label={label}
              title={label}
              // Stop propagation so selecting a theme inside a dropdown/menu
              // does not also dismiss the surrounding popover.
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                dispatch(changeTheme(value))
              }}
              className={cm(
                'flex h-7 flex-1 items-center justify-center gap-1.5 rounded-full text-xs font-medium transition-colors',
                isActive
                  ? 'bg-tokens-neutral-bg-white text-tokens-neutral-text-primary shadow-segment'
                  : 'text-tokens-neutral-text-tertiary hover:text-tokens-neutral-text-secondary'
              )}
            >
              <Icon className='shrink-0' />
              {!iconOnly && <span>{label}</span>}
            </button>
          )
        })}
      </div>
    )
  }
)
ThemeToggle.displayName = 'ThemeToggle'

export default ThemeToggle
